"use client"

import { useState } from "react"
import Link from "next/link"
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Fuel,
  Bell,
  Activity,
} from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import {
  DEMO_SITE,
  DEMO_ENGINES,
  DEMO_ALERTS,
  DEMO_DAYS,
  DEMO_TODAY,
  FUEL_PRICE_USD,
  fmtTime,
  getPeriodData,
  type Period,
} from "@/lib/demo-data"

// ── Chart config ─────────────────────────────────────────────────────────────

const runtimeChartConfig = {
  hours: { label: "Runtime (h)", color: "var(--chart-1)" },
} satisfies ChartConfig

const engineChartConfig = {
  hours: { label: "Hours", color: "var(--chart-2)" },
} satisfies ChartConfig

const periodLabels: Record<Period, string> = { today: "Today", week: "This Week", month: "This Month" }

// ── Page ─────────────────────────────────────────────────────────────────────

export default function DemoDashboardOverview() {
  const [period, setPeriod] = useState<Period>("week")
  const data = getPeriodData(period)
  const activeAlerts = DEMO_ALERTS.filter((a) => !a.acknowledged)
  const today = DEMO_TODAY
  const enginesRunning = today?.runs.filter((r) => !r.anomaly).length || 0

  const engineBarData = DEMO_ENGINES.map((e) => ({
    name: e.name.replace(" #1", ""),
    hours: data.engineHours[e.engineId] || 0,
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{DEMO_SITE.name}</h1>
          <p className="text-sm text-muted-foreground">
            {DEMO_SITE.location} · {DEMO_SITE.nodes} nodes · Last sync {DEMO_SITE.lastSync}
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border/50 p-1">
          {(["today", "week", "month"] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="engines">Engines</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Metric cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Runtime ({periodLabels[period]})</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.totalHours.toFixed(0)}h</div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  +4.2% from last {period === "today" ? "day" : period}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Fuel Estimate</CardTitle>
                <Fuel className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.totalFuel} L</div>
                <p className="text-xs text-muted-foreground">
                  ${data.totalCost.toLocaleString()} at GYD $350/L
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
                <Bell className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeAlerts.length}</div>
                <p className="text-xs text-muted-foreground">
                  {activeAlerts.length > 0 ? "Requires attention" : "All clear"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Engines Today</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{enginesRunning} / {DEMO_ENGINES.length}</div>
                <p className="text-xs text-muted-foreground">
                  {enginesRunning} running now
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts row */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Daily runtime area chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Runtime</CardTitle>
                <CardDescription>Total engine hours per day — {periodLabels[period]}</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={runtimeChartConfig} className="h-[220px] w-full">
                  <AreaChart data={data.chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      fontSize={10}
                      tickMargin={8}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      fontSize={10}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <defs>
                      <linearGradient id="fillHours" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-hours)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-hours)" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Area
                      dataKey="hours"
                      type="monotone"
                      fill="url(#fillHours)"
                      stroke="var(--color-hours)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>

            {/* Per-engine bar chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Engine Hours ({periodLabels[period]})</CardTitle>
                <CardDescription>Runtime per engine</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={engineChartConfig} className="h-[220px] w-full">
                  <BarChart data={engineBarData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={10} tickMargin={8} />
                    <YAxis tickLine={false} axisLine={false} fontSize={10} tickFormatter={(v) => `${v}h`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="hours" fill="var(--color-hours)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          {/* Today's engines + recent alerts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Today's engine events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Today&apos;s Activity</CardTitle>
                <CardDescription>{today?.weekday}, {today?.date}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Engine</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {today?.runs.filter((r) => !r.anomaly).map((run, i) => {
                      const d = run.end - run.start
                      const h = Math.floor(d / 60)
                      const m = d % 60
                      return (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{run.engineName}</TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">{fmtTime(run.start)}</TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">{fmtTime(run.end)}</TableCell>
                          <TableCell className="text-right tabular-nums">{h}h {m}m</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Recent alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Alerts</CardTitle>
                <CardDescription>{DEMO_ALERTS.length} total, {activeAlerts.length} unacknowledged</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Alert</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {DEMO_ALERTS.map((alert) => (
                      <TableRow key={alert.id} className={alert.acknowledged ? "opacity-50" : ""}>
                        <TableCell className="font-medium max-w-[200px] truncate">{alert.message}</TableCell>
                        <TableCell>
                          <Badge variant={alert.severity === "high" ? "destructive" : "secondary"} className="text-[10px]">
                            {alert.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {alert.createdAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={alert.acknowledged ? "outline" : "default"} className="text-[10px]">
                            {alert.acknowledged ? "Ack" : "New"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engines" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Engines</CardTitle>
              <CardDescription>Engine status and weekly performance</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Today</TableHead>
                    <TableHead>This Week</TableHead>
                    <TableHead className="text-right">Fuel/hr</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {DEMO_ENGINES.map((engine) => {
                    const weekHrs = data.engineHours[engine.engineId as keyof typeof data.engineHours] || 0
                    const todayRun = today?.runs.find((r) => r.engineId === engine.engineId && !r.anomaly)
                    const todayDur = todayRun ? Math.round((todayRun.end - todayRun.start) / 60 * 10) / 10 : 0
                    const isOn = todayRun && todayRun.end > 14 * 60 // still running if end is recent
                    return (
                      <TableRow key={engine.id}>
                        <TableCell className="font-medium">{engine.name}</TableCell>
                        <TableCell className="text-muted-foreground">{engine.engineType}</TableCell>
                        <TableCell>
                          <Badge variant={isOn ? "default" : "secondary"} className="text-[10px]">
                            {isOn ? "ON" : "OFF"}
                          </Badge>
                        </TableCell>
                        <TableCell className="tabular-nums">{todayDur > 0 ? `${todayDur}h` : "—"}</TableCell>
                        <TableCell className="tabular-nums">{weekHrs}h</TableCell>
                        <TableCell className="text-right text-muted-foreground tabular-nums">{engine.fuelRate} L</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Fuel breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Fuel Estimate</CardTitle>
              <CardDescription>Based on runtime × rated consumption at GYD $350/L (~${FUEL_PRICE_USD} USD)</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Engine</TableHead>
                    <TableHead className="text-right">Runtime</TableHead>
                    <TableHead className="text-right">Fuel (L)</TableHead>
                    <TableHead className="text-right">Cost (USD)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.fuel.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right tabular-nums">{row.liters}L</TableCell>
                      <TableCell className="text-right tabular-nums">${row.cost.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Object.values(data.engineHours).reduce((a, b) => a + b, 0).toFixed(0)}h
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{data.totalFuel}L</TableCell>
                    <TableCell className="text-right tabular-nums">${data.totalCost.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Alerts</CardTitle>
              <CardDescription>Sorted by most recent</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Engine</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {DEMO_ALERTS.map((alert) => (
                    <TableRow key={alert.id} className={alert.acknowledged ? "opacity-50" : ""}>
                      <TableCell className="font-medium max-w-[250px]">{alert.message}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {alert.type === "after_hours" ? "After Hours" : alert.type === "fuel" ? "Fuel" : "Anomaly"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={alert.severity === "high" ? "destructive" : alert.severity === "medium" ? "default" : "secondary"} className="text-[10px]">
                          {alert.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{alert.engineId || "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{alert.createdAt.toLocaleDateString()}</TableCell>
                      <TableCell>
                        {alert.acknowledged
                          ? <Badge variant="outline" className="text-[10px]">Acknowledged</Badge>
                          : <Badge className="text-[10px]">New</Badge>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
